import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { PieChartOutlined, ContainerOutlined } from '@ant-design/icons';
import { SidebarContextMenu } from './SidebarContextMenu';
import { usePermissions } from '@/components/PermissionsContext';
import { getModules } from '@/redux/actions';
import { menuDetails } from './menuDetailList';
import { sortBy } from 'lodash';

const DraftMenuContext = createContext({
  showDraftMenu: false,
  setShowDraftMenu: () => {},
});

export const DraftMenuProvider = ({ children }) => {
  const [showDraftMenu, setShowDraftMenu] = useState(false);

  return (
    <DraftMenuContext.Provider value={{ showDraftMenu, setShowDraftMenu }}>
      {children}
    </DraftMenuContext.Provider>
  );
};

export const useDraftMenu = () => useContext(DraftMenuContext);

const isDraftMenuItem = (name) => {
  const key = name?.toLowerCase();
  return key === 'draft' || key?.startsWith('draft-');
};

const convertToTree = (data = [], permissions = [], setOpenDropdown, showDraftMenu = false, dispatch, messageApi) => {
  const sortedData = sortBy(data, ['Priority']);
  const map = {};
  sortedData.forEach(item => {
    map[item.id] = { 
      ...item,
      path: `/${item.Name}`, 
      key: item.Name, 
      label: item.Label 
    };
  });

  const buildTree = (parentId = null, parentPath = '', setOpenDropdown, dispatch, messageApi) => {
    return sortedData
      .filter(item => item.Under === parentId)
      .map(item => {
        if (!showDraftMenu && isDraftMenuItem(item.Name)) return null;

        const permission = permissions.find(val => parseInt(val.Module_Id) === parseInt(item.id));
        if ((!item.Root && !permission?.Visible) || item.Hide_In_Menu) return null;

        const detail = menuDetails.find(val => val.key === item.Name);
        const path = `${parentPath}/${item.Name}`;
        
        const children = buildTree(item.id, path, setOpenDropdown, dispatch, messageApi);
        const menu = {
          path,
          key: item.Name,
          icon: detail?.icon || null,
          label: typeof detail?.label === 'function'
            ? detail.label({ path, label: item.Label, setOpenDropdown, isPinned: !!permission?.isPinned, moduleId: item?.id, dispatch, messageApi, isRoot: item.Root })
            : item.Label,
        };
        
        if (!item.Root) {
          return menu;
        } else if (item.Root && children.length > 0) {
          menu.children = children;
          return menu;
        }
        return null;        
      })
      .filter(Boolean); // Remove nulls
  };
  
  return buildTree(null, '', setOpenDropdown, dispatch, messageApi);
};

export const useGetSidebarMenu = (setOpenDropdown, messageApi) => {
  const { showDraftMenu } = useDraftMenu();
  const [menu, setMenu] = useState([
    {
      key: 'dashboard',
      label: (
        <SidebarContextMenu path={'/dashboard'}>
          Dashboard
        </SidebarContextMenu>
      ),
      icon: React.createElement(PieChartOutlined)
    },
  ]);
  const { token } = useSelector((state) => state.auth);
  const { modules, loading } = useSelector((state) => state.module);
  const { permissions } = usePermissions();
  const dispatch = useDispatch();
  
  useEffect(() => {
    if (token) {
      dispatch(getModules());
    }
  }, [token]);

  useEffect(() => {
    if (!loading) {
      const tree = convertToTree(modules, permissions, setOpenDropdown, showDraftMenu, dispatch, messageApi);
      setMenu([
        {
          key: 'dashboard',
          label: (
            <SidebarContextMenu path={'/dashboard'}>
              Dashboard
            </SidebarContextMenu>
          ),
          icon: React.createElement(PieChartOutlined)
        },
        ...(tree || [])
      ]);
    }
  }, [modules, loading, permissions, showDraftMenu]);

  const superMenu = [
    {
      key: 'dashboard',
      path: '/dashboard',
      label: (
        <SidebarContextMenu path={'/dashboard'}>
          Dashboard
        </SidebarContextMenu>
      ),
      icon: React.createElement(PieChartOutlined)
    },
    {
      key: 'admin-master',
      label: 'Admin Masters',
      icon: React.createElement(ContainerOutlined),
      children: [
        {
          key: 'customer',
          path: '/admin-master/customer',
          label: (
            <SidebarContextMenu path={'/admin-master/customer'}>
              Customer
            </SidebarContextMenu>
          )
        },
        {
          key: 'module',
          path: '/admin-master/module',
          label: (
            <SidebarContextMenu path={'/admin-master/module'}>
              Module
            </SidebarContextMenu>
          )
        },
        {
          key: 'customer-rights',
          path: '/admin-master/customer-rights',
          label: (
            <SidebarContextMenu path={'/admin-master/customer-rights'}>
              Customer Rights
            </SidebarContextMenu>
          )
        },
        {
          key: 'finyear',
          path: '/admin-master/finyear',
          label: (
            <SidebarContextMenu path={'/admin-master/finyear'}>
              Financial Year
            </SidebarContextMenu>
          )
        },
        {
          key: 'state',
          path: '/admin-master/state',
          label: (
            <SidebarContextMenu path={'/admin-master/state'}>
              State
            </SidebarContextMenu>
          )
        },
        {
          key: 'uom',
          path: '/admin-master/uom',
          label: (
            <SidebarContextMenu path={'/admin-master/uom'}>
              UOM
            </SidebarContextMenu>
          )
        },
        {
          key: 'industry-type',
          path: '/admin-master/industry-type',
          label: (
            <SidebarContextMenu path={'/admin-master/industry-type'}>
              Industry Type
            </SidebarContextMenu>
          )
        },
        {
          key: 'ewaybill-recharge',
          path: '/admin-master/ewaybill-recharge',
          label: (
            <SidebarContextMenu path={'/admin-master/ewaybill-recharge'}>
              EWay Bill Recharge
            </SidebarContextMenu>
          )
        },
        {
          key: 'whatsapp-plan',
          path: '/admin-master/whatsapp-plan',
          label: (
            <SidebarContextMenu path={'/admin-master/whatsapp-plan'}>
              Whatsapp Plan
            </SidebarContextMenu>
          )
        },
        {
          key: 'whatsapp-customer',
          path: '/admin-master/whatsapp-customer',
          label: (
            <SidebarContextMenu path={'/admin-master/whatsapp-customer'}>
              Whatsapp Customer
            </SidebarContextMenu>
          )
        },
        {
          key: 'superadmin-db-backup',
          path: '/admin-master/superadmin-db-backup',
          label: (
            <SidebarContextMenu path={'/admin-master/superadmin-db-backup'}>
              Db Backup
            </SidebarContextMenu>
          )
        },
        {
          key: 'bank-detail',
          path: '/admin-master/bank-detail',
          label: (
            <SidebarContextMenu path={'/admin-master/bank-detail'}>
              Bank Detail
            </SidebarContextMenu>
          )
        },
      ]
    },
  ];
  
  return { menu, superMenu };
}
